import type { Workspace, Project, Task, Message, Document } from '@/types'
import { MessageStatus } from '@/types/enums'
import { GroupingConfig } from '@/components/queries/grouping-utils'

// ============================================================================
// GROUP BY OPTION TYPES
// ============================================================================

export type WorkspaceGroupByOption = 'none' | 'creator' | 'hasUsers' | 'hasProjects' | 'hasTasks'

export type ProjectGroupByOption =
  | 'none'
  | 'status'
  | 'priority'
  | 'horizon'
  | 'workspace'
  | 'creator'

export type TaskGroupByOption =
  | 'none'
  | 'type'
  | 'status'
  | 'priority'
  | 'horizon'
  | 'assignee'
  | 'workspace'
  | 'project'

export type MessageGroupByOption =
  | 'none'
  | 'status'
  | 'creator'
  | 'workspace'
  | 'messageType'
  | 'readStatus'
  | 'priority'

export type DocumentGroupByOption = 'none' | 'type' | 'creator' | 'workspace'

export type NodeGroupByOption =
  | WorkspaceGroupByOption
  | ProjectGroupByOption
  | TaskGroupByOption
  | MessageGroupByOption
  | DocumentGroupByOption

// ============================================================================
// DISPLAY CONFIGURATION INTERFACES
// ============================================================================

export interface BaseGroupingProps {
  /** Current grouping option */
  groupBy: string
  /** Custom className for the container */
  className?: string
  /** Maximum number of items to show before truncating */
  maxVisible?: number
}

export interface WorkspaceGroupingProps extends BaseGroupingProps {
  /** Array of workspaces to group and display */
  workspaces?: Workspace[]
  /** Array of nodes (fallback) */
  nodes?: Workspace[]
  /** Array of tasks (fallback) */
  tasks?: Workspace[]
  /** Array of projects (fallback) */
  projects?: Workspace[]
  /** Current grouping option */
  groupBy: WorkspaceGroupByOption
  /** Workspace list display options */
  showDescription?: boolean
  showCreator?: boolean
  showUserCount?: boolean
  hoverMode?: boolean
  avatarSize?: 'sm' | 'md' | 'lg'
}

export interface ProjectGroupingProps extends BaseGroupingProps {
  /** Array of projects to group and display */
  projects?: Project[]
  /** Array of nodes (fallback for projects) */
  nodes?: Project[]
  /** Array of tasks (fallback for projects) */
  tasks?: Project[]
  /** Array of messages (fallback for projects) */
  messages?: Project[]
  /** Current grouping option */
  groupBy: ProjectGroupByOption
  /** Project list display options */
  showStatus?: boolean
  showPriority?: boolean
  showHorizon?: boolean
  showWorkspace?: boolean
  showUserCount?: boolean
  showDescription?: boolean
  showCreator?: boolean
  avatarSize?: 'sm' | 'md' | 'lg'
}

export interface TaskGroupingProps extends BaseGroupingProps {
  /** Array of tasks to group and display */
  tasks?: Task[]
  /** Array of nodes (fallback for tasks) */
  nodes?: Task[]
  /** Array of projects (fallback for tasks) */
  projects?: Task[]
  /** Array of messages (fallback for tasks) */
  messages?: Task[]
  /** Current grouping option */
  groupBy: TaskGroupByOption
  /** Task list display options */
  showStatus?: boolean
  showPriority?: boolean
  showHorizon?: boolean
  showType?: boolean
  showDescription?: boolean
  showWorkspace?: boolean
  showAssignee?: boolean
  showDueDate?: boolean
  showSubtasks?: boolean
  statusSize?: 'sm' | 'md'
}

export interface MessageGroupingProps extends BaseGroupingProps {
  /** Array of messages to group and display */
  messages?: Message[]
  /** Array of nodes (fallback for messages) */
  nodes?: Message[]
  /** Array of tasks (fallback for messages) */
  tasks?: Message[]
  /** Array of projects (fallback for messages) */
  projects?: Message[]
  /** Current grouping option */
  groupBy: MessageGroupByOption
  /** Custom className for the container */
  className?: string
  /** Callback when a message is selected */
  onMessageSelect?: (message: Message) => void
  /** ID of currently selected message */
  selectedMessageId?: string
  /** Search term to filter messages */
  searchTerm?: string
}

export interface DocumentGroupingProps extends BaseGroupingProps {
  /** Array of documents to group and display */
  documents?: Document[]
  /** Array of nodes (fallback for documents) */
  nodes?: Document[]
  /** Current grouping option */
  groupBy: DocumentGroupByOption
  /** Document list display options */
  showType?: boolean
  showTypeIcon?: boolean
  showDescription?: boolean
  showWorkspace?: boolean
  showCreator?: boolean
  showCreatorName?: boolean
  showDate?: boolean
  showSharedCount?: boolean
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

export const WORKSPACE_GROUPING_DEFAULTS: Partial<WorkspaceGroupingProps> = {
  showDescription: true,
  showCreator: true,
  showUserCount: true,
  hoverMode: true,
  avatarSize: 'md',
}

export const PROJECT_GROUPING_DEFAULTS: Partial<ProjectGroupingProps> = {
  showStatus: true,
  showPriority: true,
  showHorizon: false,
  showWorkspace: true,
  showUserCount: true,
  showDescription: false,
  showCreator: false,
  avatarSize: 'md',
}

export const TASK_GROUPING_DEFAULTS: Partial<TaskGroupingProps> = {
  showStatus: true,
  showPriority: true,
  showHorizon: true,
  showType: false,
  showDescription: true,
  showWorkspace: true,
  showAssignee: true,
  showDueDate: true,
  showSubtasks: true,
  statusSize: 'sm',
}

export const MESSAGE_GROUPING_DEFAULTS: Partial<MessageGroupingProps> = {
  searchTerm: '',
}

export const DOCUMENT_GROUPING_DEFAULTS: Partial<DocumentGroupingProps> = {
  showType: true,
  showTypeIcon: true,
  showDescription: true,
  showWorkspace: false,
  showCreator: true,
  showCreatorName: true,
  showDate: true,
  showSharedCount: false,
}

// ============================================================================
// GROUPING CONFIGURATIONS
// ============================================================================

export const WORKSPACE_GROUPING_CONFIG: GroupingConfig<Workspace> = {
  getGroupKeys: (workspace: Workspace, groupBy: string): string[] => {
    switch (groupBy) {
      case 'creator':
        return [workspace.creator.name]
      case 'hasUsers':
        return [workspace.users.length > 0 ? 'Has Team Members' : 'No Team Members']
      case 'hasProjects':
        return [workspace.projects.length > 0 ? 'Has Projects' : 'No Projects']
      case 'hasTasks':
        return [workspace.tasks.length > 0 ? 'Has Tasks' : 'No Tasks']
      default:
        return []
    }
  },
  noneGroupLabel: 'All Workspaces',
  itemLabels: {
    singular: 'workspace',
    plural: 'workspaces',
  },
}

export const PROJECT_GROUPING_CONFIG: GroupingConfig<Project> = {
  getGroupKeys: (project: Project, groupBy: string): string[] => {
    switch (groupBy) {
      case 'status':
        return [project.status]
      case 'priority':
        return [project.priority || 'None']
      case 'horizon':
        return [project.horizon || 'None']
      case 'workspace':
        return [project.workspace.name]
      case 'creator':
        return [project.creator.name]
      default:
        return []
    }
  },
  noneGroupLabel: 'All Projects',
  itemLabels: {
    singular: 'project',
    plural: 'projects',
  },
}

export const TASK_GROUPING_CONFIG: GroupingConfig<Task> = {
  getGroupKeys: (task: Task, groupBy: string): string[] => {
    switch (groupBy) {
      case 'type':
        return [task.type]
      case 'status':
        return [task.status]
      case 'priority':
        return [task.priority || 'None']
      case 'horizon':
        return [task.horizon || 'None']
      case 'assignee':
        return [task.assignee ? task.assignee.name : 'Unassigned']
      case 'workspace':
        return [task.workspace.name]
      case 'project':
        // Tasks can belong to multiple projects
        if (task.projects.length === 0) {
          return ['No Project']
        } else {
          return task.projects.map((p) => p.name)
        }
      default:
        return []
    }
  },
  noneGroupLabel: 'All Tasks',
  itemLabels: {
    singular: 'task',
    plural: 'tasks',
  },
}

export const MESSAGE_GROUPING_CONFIG: GroupingConfig<Message> = {
  getGroupKeys: (message: Message, groupBy: string): string[] => {
    switch (groupBy) {
      case 'status':
        return [message.status]
      case 'creator':
        return [message.creator.name]
      case 'workspace':
        return [message.workspace?.name || 'No Workspace']
      case 'messageType':
        return [message.directMessage ? 'Direct' : 'Group']
      case 'priority':
        // Messages don't have priority field, so we'll use status as a proxy
        return [message.status === MessageStatus.Published ? 'High' : 'Normal']
      default:
        return []
    }
  },
  noneGroupLabel: 'All Messages',
  itemLabels: {
    singular: 'message',
    plural: 'messages',
  },
}

export const DOCUMENT_GROUPING_CONFIG: GroupingConfig<Document> = {
  getGroupKeys: (document: Document, groupBy: string): string[] => {
    switch (groupBy) {
      case 'type':
        return [document.type]
      case 'creator':
        return [document.creator.name]
      case 'workspace':
        return [document.workspace.name]
      default:
        return []
    }
  },
  noneGroupLabel: 'All Documents',
  itemLabels: {
    singular: 'document',
    plural: 'documents',
  },
}

// ============================================================================
// CONFIGURATION MAPPINGS
// ============================================================================

export const GROUPING_CONFIGS = {
  workspace: WORKSPACE_GROUPING_CONFIG,
  project: PROJECT_GROUPING_CONFIG,
  task: TASK_GROUPING_CONFIG,
  message: MESSAGE_GROUPING_CONFIG,
  document: DOCUMENT_GROUPING_CONFIG,
} as const

export type NodeType = keyof typeof GROUPING_CONFIGS
